import { useState } from "react"
import { Check } from "lucide-react"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import TrustTierBadge, { type TrustScore } from "@/components/ui/trust-tier-badge"
import { formatDateForDisplay } from "@/lib/date-utils"
import { STATE_LABELS } from "@/lib/state-labels"
import { assistant_draft_approval_path } from "@/routes"

interface DraftAssignee {
  uid: string
  name: string
}

interface DraftRecord {
  uid: string
  name: string
  type: string
}

interface DraftRef {
  uid: string
  name: string
}

/** A recipient on a batch draft (INCO-380). Each carries the uid the tick box
 *  posts back, so a broker can drop one without redrafting the whole batch. */
interface DraftDistributionDesk {
  desk_uid: string
  name: string
  trust?: TrustScore | null
}

/** Somebody the tool could not include, and why. Shown rather than swallowed:
 *  a batch that silently drops two of twelve is a batch the broker cannot
 *  trust the count of. */
interface DraftSkipped {
  uid?: string
  person?: string
  reason?: string
}

// Mirrors Assistant::Draft#panel_json — one payload shape per kind. Discriminated
// on `kind` rather than one optional bag, because the record kinds reuse names
// the action kinds already had for a different shape: a desk message's `desk`
// is a display string, a contact's `company` is the record it belongs to.
//
// Kinds the server has retired (a follow-up, outreach, a share, a chase batch —
// INCO-449; a reply to another desk — INCO-450) are not in the union: the
// panel never lists them and their page
// answers 404, so the only place one can still arrive is an old conversation,
// which `DraftCard` answers with a line rather than a button.
interface DraftBase {
  uid: string
  state: "proposed" | "approved" | "discarded"
  // Where it came from (INCO-366). The panel does not show this — a card
  // inside the conversation that produced it needs no attribution — but the
  // standalone approval page does, because there the broker has no context at
  // all beyond a URL somebody handed them in another product.
  origin?: "panel" | "agent"
  client_name?: string | null
}

export type DraftView = DraftBase &
  (
    | {
        kind: "task"
        payload: {
          title?: string
          description?: string
          due_on?: string
          assignees?: DraftAssignee[]
          records?: DraftRecord[]
        }
      }
    | {
        kind: "opportunity"
        payload: {
          name?: string
          looking_for?: string
          category?: string
          description?: string
        }
      }
    | {
        kind: "person"
        payload: {
          name?: string
          title?: string
          email?: string
          phone?: string
          company?: DraftRef
        }
      }
    | {
        kind: "company"
        payload: { name?: string; website?: string; description?: string }
      }
    | {
        kind: "deal"
        payload: {
          name?: string
          value_cents?: number
          opportunity?: DraftRef
          owner?: DraftRef
        }
      }
    | {
        kind: "distribution"
        payload: {
          brief_uid?: string
          brief?: string
          desks?: DraftDistributionDesk[]
          skipped?: DraftSkipped[]
        }
      }
    | {
        kind: "note"
        payload: {
          record_type?: string
          record_uid?: string
          record?: string
          note?: string
        }
      }
    | {
        kind: "deal_stage"
        payload: {
          deal?: DraftRef
          from?: string
          to?: string
          event?: string
          lost_reason?: string
          note?: string
        }
      }
  )

export const KIND_LABEL: Record<DraftView["kind"], string> = {
  task: "Task draft",
  opportunity: "New opportunity",
  person: "New contact",
  company: "New counterparty",
  deal: "New deal",
  deal_stage: "Stage change",
  distribution: "Send this brief to desks",
  note: "Note draft",
}

export const APPROVED_LABEL: Record<DraftView["kind"], string> = {
  task: "Task created",
  opportunity: "Opportunity created",
  person: "Contact created",
  company: "Counterparty created",
  deal: "Deal created",
  deal_stage: "Stage changed",
  distribution: "Sent",
  note: "Note added",
}

const CREATE_LABEL: Record<string, string> = {
  opportunity: "Create opportunity",
  person: "Create contact",
  company: "Create counterparty",
  deal: "Create deal",
  note: "Add note",
}

// Was a fourth copy of the stage names (INCO-414). One map now, in
// `lib/state-labels`, mirrored from `StateLabel` in Ruby.
const STAGE_LABEL = STATE_LABELS.deal

export function csrfToken(): string {
  return (
    document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || ""
  )
}

// A proposed action from a write-capable tool, waiting on the broker. The
// approve button is deliberately the card's only accented element — it is the
// primary action of the exchange (DESIGN.md: one oxblood action per view,
// status as quiet text).
export function DraftCard({
  draft,
  accountUid,
  onChange,
}: {
  draft: DraftView
  accountUid: string
  onChange: (updated: DraftView) => void
}) {
  const [pending, setPending] = useState(false)
  const approved = draft.state === "approved"
  // One approval for a batch, but not all-or-nothing (INCO-380). Everybody
  // starts ticked, because the broker asked for the batch; unticking is the
  // edit, and it is cheaper than making them redraft.
  const [dropped, setDropped] = useState<Set<string>>(() => new Set())
  const batch = batchRecipients(draft)

  // A kind the server has since retired (INCO-449), still sitting in an old
  // conversation. Said plainly, with no button: there is nothing it could do.
  if (!(draft.kind in KIND_LABEL)) {
    return (
      <div className="rounded-lg border bg-card px-3 py-2.5" data-retired-draft>
        <p className="text-xs text-muted-foreground">
          This draft was for a feature Inco no longer has, so there is nothing it can do.
        </p>
      </div>
    )
  }

  const approve = async () => {
    setPending(true)
    try {
      const body =
        batch === null
          ? undefined
          : JSON.stringify({ only: batch.filter((r) => !dropped.has(r.uid)).map((r) => r.uid) })
      const res = await fetch(assistant_draft_approval_path(accountUid, draft.uid), {
        method: "POST",
        headers: {
          "X-CSRF-Token": csrfToken(),
          Accept: "application/json",
          ...(body ? { "Content-Type": "application/json" } : {}),
        },
        body,
      })
      const data = (await res.json()) as DraftView & { error?: string }
      if (!res.ok) {
        toast.error(data.error ?? "Approval failed")
        return
      }
      onChange(data)
    } catch {
      toast.error("Approval failed")
    } finally {
      setPending(false)
    }
  }

  return (
    <div className="rounded-lg border bg-card px-3 py-2.5">
      <p className="text-xs font-medium text-muted-foreground">
        {KIND_LABEL[draft.kind]}
      </p>
      <DraftBody draft={draft} />
      {batch !== null && !approved && (
        <RecipientPicker rows={batch} dropped={dropped} onToggle={setDropped} />
      )}
      <div className="mt-2.5">
        {approved ? (
          <p className="flex items-center gap-1 text-xs text-muted-foreground">
            <Check aria-hidden className="size-3" />
            {APPROVED_LABEL[draft.kind]}
          </p>
        ) : (
          <Button
            type="button"
            size="sm"
            onClick={approve}
            disabled={pending || (batch !== null && dropped.size >= batch.length)}
          >
            {batch === null
              ? approveLabel(draft)
              : `Send to ${batch.length - dropped.size} of ${batch.length}`}
          </Button>
        )}
      </div>
    </div>
  )
}

/** One line per recipient, uid-keyed, or null when the draft is not a batch. */
export interface BatchRow {
  uid: string
  name: string
  detail?: string
  trust?: TrustScore | null
}

/**
 * The recipients of a batch draft, one list (INCO-380). The uid is what the
 * tick box posts back, and the server matches it against the payload.
 */
export function batchRecipients(draft: DraftView): BatchRow[] | null {
  if (draft.kind === "distribution") {
    return (draft.payload.desks ?? []).map((d) => ({
      uid: d.desk_uid,
      name: d.name,
      detail: "connected desk",
      trust: d.trust,
    }))
  }

  return null
}

export function RecipientPicker({
  rows,
  dropped,
  onToggle,
}: {
  rows: BatchRow[]
  dropped: Set<string>
  onToggle: (next: Set<string>) => void
}) {
  const toggle = (uid: string) => {
    const next = new Set(dropped)
    if (next.has(uid)) next.delete(uid)
    else next.add(uid)
    onToggle(next)
  }

  return (
    <ul className="mt-2 flex flex-col gap-1" data-batch-recipients>
      {rows.map((row) => (
        <li key={row.uid} className="flex items-center gap-2">
          <Checkbox
            id={`recipient-${row.uid}`}
            checked={!dropped.has(row.uid)}
            onCheckedChange={() => toggle(row.uid)}
          />
          <label
            htmlFor={`recipient-${row.uid}`}
            className="min-w-0 flex-1 cursor-pointer truncate text-xs"
          >
            <span className={dropped.has(row.uid) ? "text-muted-foreground line-through" : ""}>
              {row.name}
            </span>
            {row.detail && <span className="text-muted-foreground"> · {row.detail}</span>}
          </label>
          {row.trust !== undefined && <TrustTierBadge trust={row.trust ?? null} />}
        </li>
      ))}
    </ul>
  )
}

export function approveLabel(draft: DraftView): string {
  if (draft.kind === "task") return "Create task"
  if (draft.kind === "deal_stage") {
    return `Move to ${STAGE_LABEL[draft.payload.to ?? ""] ?? draft.payload.to}`
  }
  return CREATE_LABEL[draft.kind]
}

// Value arrives as cents so the card, not the model, decides how it reads.
function formatValue(cents: number): string {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(cents / 100)
}

// The quiet second line under a record's name: what the desk needs to recognise
// it, joined only where a part exists so no stray separators show.
function MetaLine({ parts }: { parts: (string | undefined)[] }) {
  const shown = parts.filter(Boolean)
  if (shown.length === 0) return null
  return <p className="text-xs text-muted-foreground">{shown.join(" · ")}</p>
}

/**
 * Who was left out, and why (INCO-380). Never silent: a batch that quietly
 * drops two of twelve is a batch whose count the broker cannot trust, and the
 * reasons — not connected, chased too recently — are ones they can act on.
 */
function SkippedNote({ rows }: { rows?: DraftSkipped[] }) {
  if (!rows || rows.length === 0) return null
  return (
    <p className="text-xs text-muted-foreground" data-skipped>
      Left out: {rows.map((r) => `${r.person ?? r.uid} (${r.reason})`).join(", ")}
    </p>
  )
}

export function DraftBody({ draft }: { draft: DraftView }) {
  if (draft.kind === "task") {
    const p = draft.payload
    return (
      <div className="mt-1.5 flex flex-col gap-1">
        <p className="font-medium">{p.title}</p>
        {p.description && (
          <p className="text-sm text-muted-foreground">{p.description}</p>
        )}
        <p className="text-sm text-muted-foreground">
          {p.due_on && (
            <span>
              Due {formatDateForDisplay(p.due_on)}
            </span>
          )}
          {p.due_on && (p.assignees?.length ?? 0) > 0 && " · "}
          {(p.assignees?.length ?? 0) > 0 &&
            p.assignees!.map((a) => a.name).join(", ")}
          {(p.records?.length ?? 0) > 0 && (
            <>
              {(p.due_on || (p.assignees?.length ?? 0) > 0) && " · "}
              {p.records!.map((r) => r.name).join(", ")}
            </>
          )}
        </p>
      </div>
    )
  }

  if (draft.kind === "opportunity") {
    const p = draft.payload
    return (
      <div className="mt-1.5 flex flex-col gap-1">
        <p className="font-medium">{p.name}</p>
        <MetaLine
          parts={[
            p.looking_for ? `Looking for ${p.looking_for}` : undefined,
            p.category,
          ]}
        />
        {p.description && (
          <p className="text-sm text-muted-foreground">{p.description}</p>
        )}
      </div>
    )
  }

  if (draft.kind === "person") {
    const p = draft.payload
    return (
      <div className="mt-1.5 flex flex-col gap-1">
        <p className="font-medium">{p.name}</p>
        <MetaLine parts={[p.title, p.company?.name]} />
        <MetaLine parts={[p.email, p.phone]} />
      </div>
    )
  }

  if (draft.kind === "company") {
    const p = draft.payload
    return (
      <div className="mt-1.5 flex flex-col gap-1">
        <p className="font-medium">{p.name}</p>
        <MetaLine parts={[p.website]} />
        {p.description && (
          <p className="text-sm text-muted-foreground">{p.description}</p>
        )}
      </div>
    )
  }

  if (draft.kind === "deal") {
    const p = draft.payload
    return (
      <div className="mt-1.5 flex flex-col gap-1">
        <p className="font-medium">{p.name}</p>
        {p.value_cents != null && (
          <p className="text-sm">
            {formatValue(p.value_cents)}
          </p>
        )}
        <MetaLine
          parts={[
            p.opportunity?.name,
            p.owner ? `Owner ${p.owner.name}` : undefined,
          ]}
        />
      </div>
    )
  }

  if (draft.kind === "distribution") {
    const p = draft.payload
    return (
      <div className="mt-1.5 flex flex-col gap-1.5">
        {/*
          The brief, above the list. The names are the picker's job below;
          this is the part that is the same for everybody and the part a
          broker checks first.
        */}
        <p className="font-medium">{p.brief}</p>
        <MetaLine
          parts={[
            (p.desks?.length ?? 0) > 0
              ? `${p.desks!.length} desk${p.desks!.length === 1 ? "" : "s"}`
              : undefined,
          ]}
        />
        <SkippedNote rows={p.skipped} />
      </div>
    )
  }

  if (draft.kind === "note") {
    const p = draft.payload
    return (
      <div className="mt-1.5 flex flex-col gap-1">
        <p className="font-medium">{p.note}</p>
        {p.record && (
          <p className="text-sm text-muted-foreground">On {p.record}</p>
        )}
      </div>
    )
  }

  if (draft.kind === "deal_stage") {
    const p = draft.payload
    return (
      <div className="mt-1.5 flex flex-col gap-1">
        <p className="font-medium">{p.deal?.name}</p>
        {/*
          Both ends of the move, because "move to lost" without saying what it
          is moving from is a button somebody clicks without checking.
        */}
        <p className="text-sm text-muted-foreground">
          {STAGE_LABEL[p.from ?? ""] ?? p.from} → {STAGE_LABEL[p.to ?? ""] ?? p.to}
          {p.lost_reason ? ` · ${p.lost_reason.replace(/_/g, " ")}` : ""}
        </p>
        {p.note && <p className="text-sm text-muted-foreground">{p.note}</p>}
      </div>
    )
  }

  return null
}
